import {
    BLEND_MODES, Container, DisplayObject, Geometry, Graphics, IDestroyOptions, Matrix, Mesh, Renderer, RenderTexture,
    SCALE_MODES, Shader, Sprite, Texture,
} from "pixi.js"
import { TileMap, TileSize } from "game-common/models"
import { GroundReader, readAtlasGround, RiseMapSource } from "./ground_rise"
import {
    WATER_FACE_HEIGHT, WaterCells, WaterField, WaterRegion, bakeWaterField, bankReflection, paintWaterBank, waterBodies,
} from "./water_field"
import { setWaterPresence } from "./water_presence"

/**
 * WATER, DRAWN: the room's data-only `water` layer (common/room/ember/forest_pond.ts) as a body of water --
 * one Mesh per body with the fragment shader below, the bank baked under it (water_field.ts), and a
 * reflection texture the shader samples.
 *
 * Ground, so it sits with the ground: directly above the map1 tilemap and the raised-ground step, below
 * map2 (whose lily pads float on it) and every body. It never sorts against an entity.
 *
 * Per frame (update):
 *  - WADERS: a body whose feet stand in water is clipped at the waterline (a rectangle mask, which pixi
 *    draws as a scissor), so the water -- and anything floating on it in front of the legs -- shows where
 *    the legs were. Ankle deep at the shore, knee deep a step in, waist deep in the DEEPER core. The rings round the legs and spreading from them are drawn by the shader itself. Far
 *    cheaper than the old `submerged` crop, which rebuilt the body's art on every step in or out.
 *  - REFLECTIONS: a humanoid near or in the water has its current frame drawn upside down into the body's
 *    reflection texture, about its waterline (in the water) or the water's level below its feet (on the
 *    bank), over the baked reflection of the bank's own face.
 *
 * Every pattern is evaluated per WORLD pixel, so it stays crisp at the game's 2x and nothing swims.
 * Live tuning: window.water.enabled / .reflection / .waist, or window.water = false.
 */
export const WATER = { enabled: true, reflection: 0.55, faceHeight: WATER_FACE_HEIGHT, waist: 22 }

export const waterConfig = (): typeof WATER => {
    if (typeof window === "undefined") return WATER
    const w = window as any
    if (w.water === undefined) w.water = WATER
    const live = w.water
    if (live === false) return { ...WATER, enabled: false }
    return live && typeof live === "object" ? live : WATER
}

/** Cells of land baked round each body: the shore's blur and the bank's face both reach into it. */
const BODY_MARGIN_CELLS = 2
/** How far above the water a body still reflects in it (its feet on the bank). */
const REFLECT_REACH = 3 * TileSize
const MAX_WADERS = 8
/** The legs' ring, and how far it widens round a waist, in px. */
const RING_RADIUS = 8
const WAIST_RING_GROWTH = 3
/** How deep a wader goes in out of the deeper water (to the knee), in px; WATER.waist is how deep in it. */
const KNEE_SUBMERSION = 12

export interface WaterMapSource extends RiseMapSource {
    getWaterLayer?(): TileMap | undefined
}

/** A body standing somewhere this frame, in the world surface's coordinates. */
export interface WaterContact {
    id: string
    /** Where its feet meet the ground. */
    x: number
    y: number
    /** The frame it is showing now, for its reflection; absent for art with no frame of its own. */
    art?: DisplayObject
    /** Its renderable, which a wader is clipped on. */
    body?: Container
}

/** The rectangle keeping a wader's body above its waterline: a child of the renderable, so it moves with it. */
interface WaderClip { body: Container; clip: Graphics; line: number }

const VERT = `
attribute vec2 aVertexPosition;
attribute vec2 aUvs;
uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;
varying vec2 vUvs;
void main() {
    vUvs = aUvs;
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
}`

const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 vUvs;
uniform sampler2D uField;
uniform sampler2D uFlow;
uniform sampler2D uRefl;
uniform vec2 uSize;
uniform vec2 uOrigin;
uniform float uTime;
uniform float uFlowPx;
uniform float uReflAmt;
uniform float uDay;
uniform vec3 uR0; uniform vec3 uR1; uniform vec3 uR2; uniform vec3 uR3; uniform vec3 uR4; uniform vec3 uDeeper;
uniform vec3 uMid; uniform vec3 uLight; uniform vec3 uFoam; uniform vec3 uSpark; uniform vec3 uReflTint;
uniform vec4 uWaders[${MAX_WADERS}];

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
// Horizontal sky-glint dashes (the LPC water's own mark), and a finer shimmer drifting on its own.
float streaks(vec2 q, float t) {
    float a = vnoise(vec2(q.x / 12.0, q.y / 1.8));
    float b = vnoise(vec2(q.x / 5.0 + 7.0, q.y / 1.4 + t * 0.35));
    return a * 0.68 + b * 0.32;
}
float ringDist(vec2 p, vec2 c, float r) {
    vec2 d = (p - c) / vec2(1.0, 0.42);
    float e = length(d);
    vec2 g = vec2(d.x, d.y / 0.42) / max(e, 0.001);
    return abs(e - r) / max(length(g), 0.001);
}
void main() {
    // Everything is evaluated at a WORLD pixel's centre; p is in world px, so neighbouring bodies agree.
    vec2 local = floor(vUvs * uSize);
    vec2 p = uOrigin + local + 0.5;
    vec2 uv = (local + 0.5) / uSize;
    vec4 F = texture2D(uField, uv);
    float inD = F.r * 255.0 / 4.0;
    if (inD < 0.5) { gl_FragColor = vec4(0.0); return; }
    float shade = F.g;
    vec4 FL = texture2D(uFlow, uv);
    vec2 flow = (FL.rg * 255.0 - 128.0) / 127.0;
    float speed = length(flow);
    float still = 1.0 - smoothstep(0.12, 0.4, speed);
    float t = uTime;

    float depth = clamp((inD - 1.0) / 20.0, 0.0, 1.0);
    float band = depth * 4.0 + (vnoise(p / 6.0 + vec2(t * 0.04, 0.0)) - 0.5) * 0.9;
    vec3 col = band < 0.55 ? uR0 : band < 1.5 ? uR1 : band < 2.5 ? uR2 : band < 3.4 ? uR3 : uR4;
    // The deeper water: a darker core whose edge drifts, dithered a pixel or two where the bottom drops away.
    float drop = FL.b + (vnoise(p / 7.0 + vec2(t * 0.03, 0.0)) - 0.5) * 0.3;
    if (drop > 0.55 || (drop > 0.45 && hash(local) > 0.5)) col = uDeeper;

    // Flow-map advection: two phases half a period apart, crossfaded with the variance kept.
    float period = 2.6;
    float ph0 = fract(t / period), ph1 = fract(t / period + 0.5);
    float w0 = 1.0 - abs(2.0 * ph0 - 1.0), w1 = 1.0 - w0;
    vec2 vel = flow * uFlowPx + vec2(1.2, 0.0) * still;
    vec2 q0 = p - vel * (ph0 * period), q1 = p - vel * (ph1 * period) + vec2(31.0, 17.0);
    float n = ((streaks(q0, t) - 0.5) * w0 + (streaks(q1, t) - 0.5) * w1) / sqrt(w0 * w0 + w1 * w1) + 0.5;
    float lift = speed * 0.05;
    if (shade < 0.5) {
        if (n > 0.86 - lift) col = uLight;
        else if (n > 0.76 - lift) col = uMid;
    } else if (n > 0.84 - lift) col = mix(col, uMid, 0.5);

    // The shore: widest at the foot of a north bank (its face is in view), all but gone under a south one.
    float facing = F.b;
    float reach = mix(0.25, 1.0, facing);
    float lap = 1.0 + 1.5 * (0.5 + 0.5 * sin(t * 1.7 + vnoise(p / 11.0) * 6.283));
    float drag = vnoise(vec2(q0.x / 5.0, q0.y / 5.0)) * w0 + vnoise(vec2(q1.x / 5.0, q1.y / 5.0)) * w1;
    float foamW = mix(0.8 + drag * 2.6, lap, still) * reach;
    float fleck = vnoise(p / 2.3 + vec2(t * 0.25, 0.0));
    if (inD < foamW) col = fleck > 0.62 && shade < 0.5 ? uFoam : uLight;
    else if (inD < foamW + 1.0 + facing) col = uMid;
    float r = fract(t / 3.4 + vnoise(p / 15.0) * 0.6);
    if (still > 0.5 && abs(inD - (2.5 + r * 6.0) * reach) < 0.55 && hash(local + floor(r * 9.0)) > 0.25 + r * 0.9) col = uMid;

    // Waders: a ring round the legs (its front shows where the clipped legs were), and rings spreading out.
    for (int i = 0; i < ${MAX_WADERS}; i++) {
        vec4 wd = uWaders[i];
        if (wd.w < 0.5) continue;
        float rd0 = ringDist(p, wd.xy, wd.z);
        if (rd0 < 0.75) col = p.y > wd.y ? uFoam : uLight;
        else if (rd0 < 1.6 && p.y > wd.y) col = uMid;
        for (int k = 0; k < 2; k++) {
            float rr = fract(t / 2.0 + float(k) * 0.5 + wd.w * 0.37);
            if (ringDist(p, wd.xy, wd.z + 3.0 + rr * 16.0) < 0.6 && hash(p + floor(t * 6.0)) > rr * 0.9) col = rr < 0.45 ? uLight : uMid;
        }
    }

    // Reflections: what stands over the water, wobbling a pixel row by row.
    float wob = floor(sin(p.y * 0.85 + t * 2.3) * 1.25 + 0.5);
    vec4 R = texture2D(uRefl, (local + 0.5 + vec2(wob, 0.0)) / uSize);
    if (R.a > 0.01) col = mix(col, (R.rgb / R.a) * uReflTint, uReflAmt * R.a);

    if (shade > 0.5) col *= 0.80;

    // Sun sparkles, by day: a pixel, at the peak a short dash.
    vec2 sc = floor(p / 5.0);
    float h = hash(sc * 1.7 + 3.1);
    if (uDay > 0.5 && h > 0.955 && shade < 0.5 && depth > 0.15) {
        vec2 sp = sc * 5.0 + floor(vec2(hash(sc + 1.3), hash(sc + 2.7)) * 5.0) + 0.5;
        float tw = sin(t * 2.6 + h * 90.0);
        vec2 dd = abs(p - sp);
        if (tw > 0.90 && dd.x + dd.y < 0.6) col = uSpark;
        if (tw > 0.97 && dd.y < 0.5 && dd.x < 1.6) col = uSpark;
    }
    gl_FragColor = vec4(col, 1.0);
}`

/** LPC's own blues -- #156c99 base, #0098b2 / #00cfdf marks -- with a deeper end and a lighter shallow. */
const PALETTE = {
    ramp: ["#2a86a8", "#1d78a2", "#156c99", "#125f8c", "#0f527c"],
    /** The waist-deep core: one step on down the ramp. */
    deeper: "#0c4570",
    mid: "#0d93b0", light: "#4fd0dd", foam: "#c5eeea", spark: "#f4fffd", reflection: "#6f8fa0",
}
const rgb = (hex: string): Float32Array =>
    new Float32Array([1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255))

interface Body {
    key: string
    region: WaterRegion
    /** What it was baked from; re-baked whenever that differs. */
    signature: string
    /** Waiting for a bake, which runs a stage a frame (see tick); the old art stays up until it is done. */
    queued: boolean
    pending?: { ground: Uint8ClampedArray; field?: WaterField; bank?: Uint8ClampedArray }
    field?: WaterField
    textures: Texture[]
    reflection?: RenderTexture
    reflectionBase?: Sprite
    mesh?: Mesh<Shader>
    bank?: Sprite
    uniforms?: any
    /** Reflections drawn last frame: when there are none now either, the texture is left as it is. */
    reflected: number
}

const regionGeometry = (x: number, y: number, w: number, h: number, region: WaterRegion): Geometry => {
    const u0 = (x - region.x0) / region.w, v0 = (y - region.y0) / region.h
    const u1 = (x + w - region.x0) / region.w, v1 = (y + h - region.y0) / region.h
    return new Geometry()
        .addAttribute("aVertexPosition", [x, y, x + w, y, x + w, y + h, x, y + h], 2)
        .addAttribute("aUvs", [u0, v0, u1, v0, u1, v1, u0, v1], 2)
        .addIndex([0, 1, 2, 0, 2, 3])
}

const canvasTexture = (rgba: Uint8ClampedArray, w: number, h: number): Texture | undefined => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const context = canvas.getContext("2d")
    if (!context) return undefined
    context.putImageData(new ImageData(rgba, w, h), 0, 0)
    return Texture.from(canvas, { scaleMode: SCALE_MODES.NEAREST })
}

export class WaterLayer extends Container {
    private source?: WaterMapSource
    private bodies = new Map<string, Body>()
    private queue: Body[] = []
    private startMs = typeof performance === "undefined" ? 0 : performance.now()
    private clips = new Map<string, WaderClip>()
    private eraser?: Graphics

    constructor(private readGround: GroundReader = readAtlasGround) {
        super()
        this.interactiveChildren = false
    }

    /** After every full map render: which bodies exist, and which changed. */
    sync = (source: WaterMapSource | undefined): void => {
        const config = waterConfig()
        const water = source?.getWaterLayer?.()
        if (!source || !water || !config.enabled) {
            this.source = source
            this.clearBodies()
            this.visible = false
            return
        }
        if (source.roomId !== this.source?.roomId) this.clearBodies()
        this.source = source
        this.visible = true
        const width = source.width(), height = source.height()
        const cells = cellsOf(water, width, height)
        const ground = source.resolveTileMapFromLayerId("map1")
        const wanted = new Set<string>()
        for (const found of waterBodies(cells, width, height)) {
            const cx0 = Math.max(0, found.x0 - BODY_MARGIN_CELLS), cy0 = Math.max(0, found.y0 - BODY_MARGIN_CELLS)
            const cx1 = Math.min(width - 1, found.x1 + BODY_MARGIN_CELLS), cy1 = Math.min(height - 1, found.y1 + BODY_MARGIN_CELLS)
            const key = `${cx0},${cy0},${cx1},${cy1}`
            wanted.add(key)
            const parts = [`${config.faceHeight}`]
            for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) parts.push(`${cells(cx, cy)}${ground?.[cx]?.[cy] ?? ""}`)
            const signature = parts.join(",")
            let body = this.bodies.get(key)
            if (!body) {
                body = { key, signature: "", queued: false, textures: [], reflected: 0,
                    region: { x0: cx0 * TileSize, y0: cy0 * TileSize, w: (cx1 - cx0 + 1) * TileSize, h: (cy1 - cy0 + 1) * TileSize } }
                this.bodies.set(key, body)
            }
            if (body.signature !== signature) {
                body.signature = signature
                body.pending = undefined
                body.queued = true
            }
        }
        this.bodies.forEach((body, key) => { if (!wanted.has(key)) this.dropBody(body) })
        this.queue = Array.from(this.bodies.values()).filter(body => body.queued)
        this.publishPresence()
    }

    /**
     * Bake whatever is waiting -- ONE stage a frame (read the ground; the shore; the bank; the art), so a
     * budget machine never stalls on a whole pond at once -- and move the time on. An atlas still decoding
     * leaves the first stage for the next frame.
     */
    tick = (): void => {
        if (!this.source) return
        const body = this.queue[0]
        if (body) {
            const config = waterConfig()
            const { region } = body
            const cells = cellsOf(this.source.getWaterLayer?.(), this.source.width(), this.source.height())
            if (!body.pending) {
                const ground = this.readGround(this.source, region.x0, region.y0, region.w, region.h)
                if (ground) body.pending = { ground }
            } else if (!body.pending.field) {
                body.pending.field = bakeWaterField(cells, region, config.faceHeight)
            } else if (!body.pending.bank) {
                body.pending.bank = paintWaterBank(body.pending.ground, body.pending.field.mask, region, config.faceHeight)
            } else {
                this.queue.shift()
                body.queued = false
                this.bake(body, body.pending.ground, body.pending.field, body.pending.bank)
                body.pending = undefined
                this.publishPresence()
            }
        }
        const now = (typeof performance === "undefined" ? 0 : performance.now()) - this.startMs
        // Wrapped, so a long session never runs the shader's time past what a mediump float can hold.
        this.bodies.forEach(body => { if (body.uniforms) body.uniforms.uTime = (now / 1000) % 600 })
    }

    /** px of water over the ground at a world point, or undefined on dry land (or before the bake). */
    depthAt = (px: number, py: number): number | undefined => this.wadeAt(px, py)?.depth

    /**
     * How a body standing at a world point wades: `depth` px from the shore, `deeper` 0..1 into the
     * waist-deep core, and `sink`, how far up its body the waterline comes. Undefined on dry land.
     */
    wadeAt = (px: number, py: number, waist = waterConfig().waist): { depth: number; deeper: number; sink: number } | undefined => {
        for (const body of this.bodies.values()) {
            const { x0, y0, w, h } = body.region
            const x = Math.floor(px - x0), y = Math.floor(py - y0)
            if (!body.field || x < 0 || y < 0 || x >= w || y >= h) continue
            const i = y * w + x
            if (!body.field.mask[i]) return undefined
            const depth = body.field.depth[i], deeper = body.field.deeper[i]
            const knee = Math.max(2, Math.min(KNEE_SUBMERSION, depth * 0.55))
            return { depth, deeper, sink: Math.round(knee + (Math.max(knee, waist) - knee) * deeper) }
        }
        return undefined
    }

    /**
     * Once a frame, after the bodies are laid out: who wades, and what reflects. `surface` is the world
     * surface the bodies stand in; `daylight` 0..1 turns the sun's sparkles off after dark.
     */
    update = (contacts: WaterContact[], surface: Container, renderer: Renderer | undefined, daylight = 1): Set<string> => {
        const wading = new Set<string>()
        const config = waterConfig()
        this.bodies.forEach(body => {
            if (!body.uniforms || !body.mesh) return
            body.uniforms.uReflAmt = config.reflection
            body.uniforms.uDay = daylight
            const waders: Array<{ contact: WaterContact; line: number; ring: number }> = []
            const reflect: Array<{ art: DisplayObject; mirror: number }> = []
            const { x0, y0, w, h } = body.region
            for (const contact of contacts) {
                if (contact.x < x0 || contact.x >= x0 + w || contact.y < y0 - REFLECT_REACH || contact.y >= y0 + h) continue
                const wade = this.wadeAt(contact.x, contact.y, config.waist)
                if (wade && waders.length < MAX_WADERS) {
                    const line = Math.round(contact.y) - wade.sink
                    waders.push({ contact, line, ring: RING_RADIUS + WAIST_RING_GROWTH * wade.deeper })
                    wading.add(contact.id)
                    this.clipAt(contact, line)
                    if (contact.art) reflect.push({ art: contact.art, mirror: line })
                } else if (!wade && contact.art && this.waterBelow(contact.x, contact.y)) {
                    reflect.push({ art: contact.art, mirror: Math.round(contact.y) + config.faceHeight })
                }
            }
            const list = body.uniforms.uWaders as Float32Array
            list.fill(0)
            waders.forEach(({ contact, line, ring }, i) => list.set([Math.round(contact.x), line + 0.5, ring, 1 + i], i * 4))
            if (renderer) this.renderReflections(body, reflect, waders, surface, renderer)
        })
        this.clips.forEach((_, id) => { if (!wading.has(id)) this.releaseClip(id) })
        return wading
    }

    /** Water somewhere in the 64px under a point: close enough for what stands there to show in it. */
    private waterBelow = (x: number, y: number): boolean => {
        for (let k = 4; k <= 64; k += 6) if (this.depthAt(x, y + k) !== undefined) return true
        return false
    }

    private clipAt = (contact: WaterContact, line: number): void => {
        const body = contact.body
        if (!body || body.destroyed) return
        let entry = this.clips.get(contact.id)
        if (entry && (entry.body !== body || entry.clip.destroyed)) {
            this.releaseClip(contact.id)
            entry = undefined
        }
        if (!entry) {
            // Something else is clipping this body already (a stair's opening): leave it be.
            if (body.mask) return
            const clip = new Graphics()
            clip.name = "water-clip"
            body.addChild(clip)
            body.mask = clip
            entry = { body, clip, line: NaN }
            this.clips.set(contact.id, entry)
        }
        const local = (line - body.y) / (body.scale.y || 1)
        if (entry.line === local) return
        entry.clip.clear().beginFill(0xffffff).drawRect(-96, local - 256, 192, 256).endFill()
        entry.line = local
    }

    private releaseClip = (id: string): void => {
        const entry = this.clips.get(id)
        if (!entry) return
        if (!entry.body.destroyed && entry.body.mask === entry.clip) entry.body.mask = null
        if (!entry.clip.destroyed) entry.clip.destroy()
        this.clips.delete(id)
    }

    private renderReflections = (body: Body, reflect: Array<{ art: DisplayObject; mirror: number }>,
        waders: Array<{ contact: WaterContact; line: number }>, surface: Container, renderer: Renderer): void => {
        if (!body.reflection || !body.reflectionBase) return
        if (!reflect.length && !body.reflected) return
        const { x0, y0 } = body.region
        renderer.render(body.reflectionBase, { renderTexture: body.reflection, clear: true })
        const inverse = surface.worldTransform.clone().invert()
        for (const { art, mirror } of reflect) {
            if (!art.worldVisible || art.destroyed) continue
            // world (the surface's space) -> flipped about the waterline -> the reflection texture's pixels
            const flip = new Matrix(1, 0, 0, -1, -x0, 2 * mirror - y0)
            renderer.render(art, { renderTexture: body.reflection, clear: false, skipUpdateTransform: true,
                transform: flip.append(inverse) })
        }
        // A wader's legs, flipped, land ABOVE its waterline: rub them out -- the water reflects only what is above it.
        if (waders.length) {
            const eraser = this.eraser ??= Object.assign(new Graphics(), { blendMode: BLEND_MODES.ERASE })
            eraser.clear().beginFill(0xffffff)
            waders.forEach(({ contact, line }) => eraser.drawRect(Math.round(contact.x) - x0 - 24, line - y0 - 64, 48, 64))
            eraser.endFill()
            renderer.render(eraser, { renderTexture: body.reflection, clear: false })
        }
        // The bank goes over the bodies again: it stands between them and the water.
        if (reflect.length) renderer.render(body.reflectionBase, { renderTexture: body.reflection, clear: false })
        body.reflected = reflect.length
    }

    private bake = (body: Body, ground: Uint8ClampedArray, field: WaterField, bank: Uint8ClampedArray): void => {
        this.releaseArt(body)
        const config = waterConfig()
        const { region } = body
        body.field = field
        const reflection = bankReflection(ground, bank, field.mask, region, config.faceHeight)
        const options = { width: region.w, height: region.h, scaleMode: SCALE_MODES.NEAREST }
        const fieldTexture = Texture.fromBuffer(field.field, region.w, region.h, options)
        const flowTexture = Texture.fromBuffer(field.flow, region.w, region.h, options)
        body.textures.push(fieldTexture, flowTexture)
        body.reflection = RenderTexture.create({ width: region.w, height: region.h, scaleMode: SCALE_MODES.NEAREST })
        const reflectionTexture = canvasTexture(reflection, region.w, region.h)
        if (reflectionTexture) body.textures.push(reflectionTexture)
        body.reflectionBase = new Sprite(reflectionTexture ?? Texture.EMPTY)
        body.uniforms = {
            uField: fieldTexture, uFlow: flowTexture, uRefl: body.reflection,
            uSize: new Float32Array([region.w, region.h]), uOrigin: new Float32Array([region.x0, region.y0]),
            uTime: 0, uFlowPx: 16, uReflAmt: config.reflection, uDay: 1,
            uR0: rgb(PALETTE.ramp[0]), uR1: rgb(PALETTE.ramp[1]), uR2: rgb(PALETTE.ramp[2]), uR3: rgb(PALETTE.ramp[3]),
            uR4: rgb(PALETTE.ramp[4]), uDeeper: rgb(PALETTE.deeper), uMid: rgb(PALETTE.mid), uLight: rgb(PALETTE.light), uFoam: rgb(PALETTE.foam),
            uSpark: rgb(PALETTE.spark), uReflTint: rgb(PALETTE.reflection), uWaders: new Float32Array(MAX_WADERS * 4),
        }
        body.mesh = new Mesh(regionGeometry(region.x0, region.y0, region.w, region.h, region), Shader.from(VERT, FRAG, body.uniforms))
        body.mesh.name = "water"
        this.addChild(body.mesh)
        const bankTexture = canvasTexture(bank, region.w, region.h)
        if (bankTexture) {
            body.textures.push(bankTexture)
            body.bank = new Sprite(bankTexture)
            body.bank.position.set(region.x0, region.y0)
            this.addChild(body.bank)
        }
        body.reflected = 1 // draw the bank's reflection on the first update
    }

    /** The baked shore where there is one; the cells while a body still waits for its bake. */
    private isWater = (px: number, py: number): boolean => {
        for (const body of this.bodies.values()) {
            const { x0, y0, w, h } = body.region
            const x = Math.floor(px - x0), y = Math.floor(py - y0)
            if (x < 0 || y < 0 || x >= w || y >= h) continue
            if (body.field) return body.field.mask[y * w + x] === 1
            const water = this.source?.getWaterLayer?.()
            return !!this.source && cellsOf(water, this.source.width(), this.source.height())(
                Math.floor(px / TileSize), Math.floor(py / TileSize)) > 0
        }
        return false
    }

    /** grassAt and the footsteps ask the baked shore, not the cells, whether a point is water. */
    private publishPresence = (): void => {
        const roomId = this.source?.roomId
        if (!roomId) return
        setWaterPresence(roomId, this.bodies.size ? this.isWater : undefined)
    }

    private releaseArt = (body: Body): void => {
        body.mesh?.destroy()
        body.bank?.destroy()
        body.reflectionBase?.destroy()
        body.reflection?.destroy(true)
        body.textures.forEach(texture => texture.destroy(true))
        body.mesh = body.bank = body.reflectionBase = body.reflection = body.uniforms = undefined
        body.textures = []
        body.field = undefined
    }

    private dropBody = (body: Body): void => {
        this.releaseArt(body)
        body.queued = false
        body.pending = undefined
        this.bodies.delete(body.key)
        this.queue = this.queue.filter(waiting => waiting !== body)
    }

    private clearBodies = (): void => {
        this.bodies.forEach(body => this.releaseArt(body))
        this.bodies.clear()
        this.queue = []
        if (this.source?.roomId) setWaterPresence(this.source.roomId, undefined)
    }

    /** True while bodies are still waiting to bake. */
    get baking(): boolean {
        return this.queue.length > 0
    }

    destroy(options?: IDestroyOptions | boolean): void {
        Array.from(this.clips.keys()).forEach(this.releaseClip)
        this.eraser?.destroy()
        this.clearBodies()
        super.destroy(options)
    }
}

const cellsOf = (water: TileMap | undefined, width: number, height: number): WaterCells => (x, y) => {
    if (!water || x < 0 || y < 0 || x >= width || y >= height) return 0
    const v = water[x]?.[y]
    return v && v !== "0" ? Number(v) || 1 : 0
}
